/* Feb-2026 · Admin Overview
 *
 * Pro-writer-only summary that surfaces every operational panel's health
 * in one place. Red items are pushed to a dedicated "Needs Admin Review"
 * strip at the top so the admin knows exactly which panel to open.
 *
 * Backend: GET /api/admin/overview
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { PanelHeader } from './_shared';
import {
  ShieldAlert, RefreshCw, ExternalLink, AlertTriangle, Info, CheckCircle2,
  Loader2, Gauge, ClipboardCheck,
} from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
const http = axios.create({ baseURL: API, withCredentials: true });

const SEV_META = {
  red:   { label: 'Needs Admin Review', bg: 'bg-rose-50 border-rose-300 text-rose-900',    dotBg: 'bg-rose-500',    icon: AlertTriangle },
  amber: { label: 'Watch',               bg: 'bg-amber-50 border-amber-300 text-amber-900', dotBg: 'bg-amber-500',   icon: Info },
  green: { label: 'Healthy',             bg: 'bg-emerald-50 border-emerald-200 text-emerald-900', dotBg: 'bg-emerald-500', icon: CheckCircle2 },
};

function SeverityCard({ panel, context = 'grid' }) {
  const meta = SEV_META[panel.severity] || SEV_META.green;
  const Icon = meta.icon;
  const href = `${panel.route}?tab=${encodeURIComponent(panel.tab)}`;
  return (
    <div
      className={`flex flex-col justify-between rounded-md border p-3 ${meta.bg}`}
      data-testid={`admin-overview-card-${panel.panel}-${context}`}
    >
      <div>
        <div className="mb-1 flex items-center gap-2">
          <span className={`inline-block h-2 w-2 rounded-full ${meta.dotBg}`} />
          <div className="text-[11px] font-semibold uppercase tracking-wide">{panel.label}</div>
          <Icon className="ml-auto h-3.5 w-3.5 opacity-70" />
        </div>
        <div className="text-sm">{panel.message}</div>
        {panel.urgent_items && panel.urgent_items.length > 0 && (
          <ul className="mt-2 space-y-0.5 border-t border-current/20 pt-2 text-[11.5px]">
            {panel.urgent_items.slice(0, 4).map((it) => (
              <li key={it.id || it.title} className="truncate">
                • {it.title}
                {typeof it.days_out === 'number' && (
                  <span className="ml-1 opacity-70">
                    ({it.days_out < 0 ? `${Math.abs(it.days_out)} d overdue` : `${it.days_out} d left`})
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="mt-3 flex items-center justify-between border-t border-current/20 pt-2">
        <span className="text-[10.5px] uppercase tracking-wider opacity-70">
          Owner: {panel.responsible_scope || 'pro_writer'}
        </span>
        <Button
          asChild
          size="sm"
          variant="ghost"
          className="h-6 gap-1 px-2 text-[11px]"
        >
          <Link
            to={href}
            data-testid={`admin-overview-open-${panel.panel}-${context}`}
          >
            Open <ExternalLink className="h-3 w-3" />
          </Link>
        </Button>
      </div>
    </div>
  );
}

export default function AdminOverviewPanel() {
  const [data, setData] = useState(null);
  const [closed, setClosed] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const reload = useCallback(async () => {
    setBusy(true);
    setErr('');
    try {
      const [ov, ci] = await Promise.all([
        http.get('/admin/overview'),
        http.get('/admin/overview/closed-issues').catch(() => ({ data: { items: [], count: 0 } })),
      ]);
      setData(ov.data);
      setClosed(ci.data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setErr(detail || 'Unable to load admin overview.');
      if (e?.response?.status === 403) {
        toast.error('Admin Overview is restricted to full Admin (pro_writer).');
      }
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const panels = data?.panels || [];
  const redFlags = data?.red_flags || [];
  const amberFlags = data?.amber_flags || [];

  return (
    <div className="space-y-4" data-testid="admin-overview-panel">
      <PanelHeader
        icon={Gauge}
        title="Admin Overview"
        subtitle="Cross-panel health at a glance. Red items surface here so the top-tier Admin knows exactly which panel needs review."
      />

      <div className="flex items-center gap-2">
        <Button size="sm" variant="outline" onClick={reload} disabled={busy} data-testid="admin-overview-refresh">
          {busy ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="mr-2 h-3.5 w-3.5" />}
          Refresh
        </Button>
        {data && (
          <div className="ml-auto flex items-center gap-3 text-[11px] text-muted-foreground">
            <span className="inline-flex items-center gap-1">
              <span className="h-2 w-2 rounded-full bg-rose-500" /> {data.counts.red} red
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="h-2 w-2 rounded-full bg-amber-500" /> {data.counts.amber} amber
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="h-2 w-2 rounded-full bg-emerald-500" /> {data.counts.green} healthy
            </span>
          </div>
        )}
      </div>

      {err && (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
          {err}
        </div>
      )}

      {redFlags.length > 0 && (
        <div className="rounded-md border-2 border-rose-400 bg-rose-50 p-3" data-testid="admin-overview-red-strip">
          <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-rose-800">
            <ShieldAlert className="h-4 w-4" /> Needs Admin Review · {redFlags.length}
          </div>
          <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
            {redFlags.map((p) => (
              <SeverityCard key={p.panel} panel={p} context="red" />
            ))}
          </div>
        </div>
      )}

      {amberFlags.length > 0 && (
        <div className="rounded-md border border-amber-300 bg-amber-50/70 p-3" data-testid="admin-overview-amber-strip">
          <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-amber-800">
            <Info className="h-4 w-4" /> Watch · {amberFlags.length}
          </div>
          <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
            {amberFlags.map((p) => (
              <SeverityCard key={p.panel} panel={p} context="amber" />
            ))}
          </div>
        </div>
      )}

      <div>
        <div className="mb-2 text-[11px] uppercase tracking-wide text-muted-foreground">
          All operational panels
        </div>
        <div className="grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-3" data-testid="admin-overview-grid">
          {panels.map((p) => (
            <SeverityCard key={p.panel} panel={p} context="grid" />
          ))}
        </div>
      </div>

      {data?.generated_at && (
        <div className="text-right text-[10.5px] text-muted-foreground">
          Generated {new Date(data.generated_at).toLocaleString()}
        </div>
      )}

      {closed && closed.items && closed.items.length > 0 && (
        <div className="rounded-md border border-emerald-200 bg-emerald-50/60 p-3" data-testid="admin-overview-closed">
          <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-emerald-900">
            <ClipboardCheck className="h-4 w-4" /> Closed Issues · {closed.items.length}
          </div>
          <ul className="space-y-1">
            {closed.items.slice(0, 12).map((c) => (
              <li key={c.id} className="flex flex-wrap items-baseline gap-2 border-t border-emerald-200/40 pt-1 text-[12px]">
                <span className="rounded-full bg-emerald-200/60 px-2 py-0.5 text-[10px] uppercase tracking-wider text-emerald-900">
                  {c.panel}
                </span>
                <span className="flex-1">{c.detail}</span>
                <span className="text-[11px] text-muted-foreground">
                  by {c.actor} · {new Date(c.resolved_at).toLocaleDateString()}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
